import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { formatDate, todayStr } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { CalendarDays, CheckCircle2, Circle, Plus, Trash2, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TaskEditDialog } from '@/components/TaskEditDialog';

const FU_STYLE = {
  overdue: { dot: 'bg-red-500', box: 'border-red-500/40 bg-red-950/20', label: 'Vencido', text: 'text-red-400' },
  today: { dot: 'bg-yellow-400', box: 'border-yellow-400/40 bg-yellow-400/[0.06]', label: 'Hoy', text: 'text-yellow-300' },
  upcoming: { dot: 'bg-[#6BCB77]', box: 'border-white/5 bg-[#16302B]', label: 'Próximo', text: 'text-[#6BCB77]' },
  done: { dot: 'bg-white/30', box: 'border-white/5 bg-[#16302B] opacity-60', label: 'Hecho', text: 'text-white/40' },
};

const DayFollowUps = ({ items, onOpen }) => !items.length ? null : (
  <div data-testid="day-follow-ups" className="space-y-2 mb-4">
    <h3 className="text-xs uppercase tracking-wider text-white/50 font-medium">Seguimientos de leads</h3>
    {items.map(ev => {
      const st = FU_STYLE[ev.status] || FU_STYLE.upcoming;
      return (
        <div key={ev.id} onClick={() => onOpen(ev)} role="button" data-testid={`follow-up-event-${ev.id}`}
          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer hover:border-[#A38560]/40 transition-colors ${st.box}`}>
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${st.dot}`} />
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-medium truncate ${ev.completed ? 'line-through text-white/40' : 'text-white'}`}>{ev.client}</p>
            <p className="text-xs text-white/50 truncate">{ev.title.replace(/^Seguimiento: /, '')}</p>
          </div>
          {ev.time && <span className="text-xs text-white/60 tabular-nums shrink-0">{ev.time}</span>}
          <span className={`text-[10px] uppercase font-semibold tracking-wider shrink-0 ${st.text}`}>{st.label}</span>
          <ChevronRight size={16} className="text-white/30 shrink-0" />
        </div>
      );
    })}
  </div>
);

export default function Agenda() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();
  const openEvent = ev => {
    if ((ev.type === 'crm_task' || ev.type === 'follow_up') && ev.lead_id) navigate(`/crm/${ev.lead_id}`);
    else if (ev.deal_id) navigate(`/negocios/${ev.deal_id}`);
  };
  const [taskForm, setTaskForm] = useState({ title: '', description: '', due_date: todayStr(), assigned_to: '', assigned_to_name: '' });

  const load = async () => {
    try {
      const [e, t, u] = await Promise.all([api.get('/agenda/events'), api.get('/tasks'), api.get('/users')]);
      setEvents(e);
      setTasks(t);
      setUsers(u);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

  const toggleEvent = async (ev) => {
    if (ev.deal_id && ev.field) {
      try {
        await api.put(`/deals/${ev.deal_id}/toggle-complete`, { field: ev.field });
        load();
      } catch { toast.error('Error'); }
    }
  };

  const saveTask = async () => {
    if (!taskForm.title) return toast.error('El titulo es obligatorio');
    try {
      await api.post('/tasks', taskForm);
      toast.success('Tarea creada');
      setTaskForm({ title: '', description: '', due_date: todayStr(), assigned_to: '', assigned_to_name: '' });
      setShowForm(false);
      load();
    } catch { toast.error('Error al crear tarea'); }
  };

  const toggleTask = async (task) => {
    const newStatus = task.status === 'completada' ? 'pendiente' : 'completada';
    try {
      await api.put(`/tasks/${task.id}`, { status: newStatus });
      load();
    } catch { toast.error('Error'); }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try { await api.del(`/tasks/${taskId}`); load(); } catch { toast.error('Error'); }
  };

  const eventDates = events.filter(e => e.type !== 'follow_up').map(e => new Date(e.date + 'T12:00:00'));
  const taskDates = tasks.filter(t => t.due_date).map(t => new Date(t.due_date + 'T12:00:00'));
  const allDates = [...eventDates, ...taskDates];
  const fuEvents = events.filter(e => e.type === 'follow_up');
  const fuDates = st => fuEvents.filter(e => e.status === st).map(e => new Date(e.date + 'T12:00:00'));

  const selectedDateStr = selectedDate.toLocaleDateString('en-CA');
  const dayEvents = events.filter(e => e.date === selectedDateStr && e.type !== 'follow_up');
  const dayFollowUps = fuEvents.filter(e => e.date === selectedDateStr).sort((a, b) => (a.time || '99').localeCompare(b.time || '99'));
  const dayTasks = tasks.filter(t => t.due_date === selectedDateStr);

  const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 rounded-xl";
  const selectContentCls = "bg-[#0A1F1A] border-white/10 text-white";

  return (
    <div data-testid="agenda-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white">Agenda y Tareas</h1>
        <button onClick={() => setShowForm(!showForm)} data-testid="new-task-btn"
          className="flex items-center gap-2 bg-gradient-to-r from-[#A38560] to-[#8B6F47] hover:from-[#8B6F47] hover:to-[#725A3A] text-[#03110D] font-semibold px-5 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98]">
          <Plus size={18} strokeWidth={1.5} /> Nueva Tarea
        </button>
      </div>

      {showForm && (
        <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-6 space-y-4">
          <h2 className="font-outfit text-lg font-medium text-white">Crear Tarea</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Titulo *</Label>
              <Input data-testid="task-title" value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="Nombre de la tarea" className={inputCls} />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha limite</Label>
              <Input type="date" value={taskForm.due_date} onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Descripcion</Label>
            <Textarea value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} placeholder="Detalles..." className="bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] min-h-[60px] rounded-xl" />
          </div>
          {user?.role === 'admin' && (
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Asignar a</Label>
              <Select value={taskForm.assigned_to} onValueChange={v => { const u = users.find(x => x.id === v); setTaskForm({ ...taskForm, assigned_to: v, assigned_to_name: u?.name || '' }); }}>
                <SelectTrigger className={inputCls}><SelectValue placeholder="Seleccionar persona" /></SelectTrigger>
                <SelectContent className={selectContentCls}>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={saveTask} data-testid="save-task-btn"
              className="bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98]">Guardar Tarea</button>
            <button onClick={() => setShowForm(false)} className="px-6 py-2.5 border border-white/20 text-white rounded-xl hover:bg-white/10 transition-all text-sm font-medium">Cancelar</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Calendar */}
        <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
          <Calendar mode="single" selected={selectedDate} onSelect={d => d && setSelectedDate(d)}
            modifiers={{ hasEvent: allDates, fuUpcoming: fuDates('upcoming'), fuToday: fuDates('today'), fuOverdue: fuDates('overdue') }}
            modifiersStyles={{ hasEvent: { fontWeight: 'bold', backgroundColor: '#A38560', color: '#03110D', borderRadius: '50%' },
              fuUpcoming: { boxShadow: 'inset 0 0 0 2px #6BCB77', borderRadius: '50%' },
              fuToday: { boxShadow: 'inset 0 0 0 2px #FACC15', borderRadius: '50%' },
              fuOverdue: { boxShadow: 'inset 0 0 0 2px #EF4444', borderRadius: '50%' } }}
            className="w-full text-white
              [&_.rdp-day]:text-white
              [&_.rdp-day_button]:text-white
              [&_.rdp-head_cell]:text-white/50
              [&_.rdp-caption]:text-white
              [&_.rdp-nav_button]:text-white
              [&_.rdp-day_today]:bg-white/10
              [&_.rdp-day_selected]:bg-[#A38560]
              [&_.rdp-day_selected]:text-[#03110D]
              [&_.rdp-button:hover]:bg-white/10
              [&_.rdp-day_outside]:text-white/20
            " />
        </div>

        {/* Day Events */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-6">
            <h2 className="font-outfit text-lg font-medium text-white mb-3 flex items-center gap-2">
              <CalendarDays size={18} strokeWidth={1.5} className="text-[#A38560]" />
              {selectedDate.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h2>

            <DayFollowUps items={dayFollowUps} onOpen={openEvent} />

            {dayEvents.length > 0 && (
              <div className="space-y-2 mb-4">
                <h3 className="text-xs uppercase tracking-wider text-white/50 font-medium">Actividad</h3>
                {dayEvents.map(ev => (
                  <div key={ev.id} onClick={() => openEvent(ev)} data-testid={`agenda-event-${ev.id}`} role={(ev.deal_id || ev.lead_id) ? 'button' : undefined}
                    className={`flex items-center gap-3 p-3 bg-[#16302B] rounded-xl border transition-colors ${(ev.deal_id || ev.lead_id) ? 'cursor-pointer hover:border-[#A38560]/40' : ''} ${ev.overdue ? 'border-red-500/50 bg-red-950/20' : 'border-white/5'}`}>
                    {(ev.type === 'step' || ev.type === 'payment') ? (
                      <button onClick={e => { e.stopPropagation(); toggleEvent(ev); }} data-testid={`toggle-event-${ev.id}`} className="shrink-0">
                        {ev.completed ? <CheckCircle2 size={18} className="text-[#6BCB77]" /> : <Circle size={18} className={ev.overdue ? 'text-red-400' : 'text-white/30'} />}
                      </button>
                    ) : ev.type === 'crm_task' ? (
                      <Badge className="bg-purple-500/10 text-purple-400 border-0">CRM</Badge>
                    ) : (
                      <Badge className={
                        ev.type === 'closing' ? 'bg-[#6BCB77]/10 text-[#6BCB77] border-0' :
                        ev.type === 'visita' ? 'bg-white/5 text-white/70 border-0' :
                        ev.type === 'oferta' ? 'bg-[#A38560]/10 text-[#A38560] border-0' :
                        'bg-[#6BCB77]/10 text-[#6BCB77] border-0'
                      }>
                        {ev.type === 'closing' ? 'Cierre' : ev.type === 'visita' ? 'Visita' : ev.type === 'oferta' ? 'Oferta' : 'Paso'}
                      </Badge>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate ${ev.completed ? 'line-through text-white/40' : ev.overdue ? 'text-red-400' : 'text-white'}`}>{ev.title}</p>
                      <p className="text-xs text-white/50 truncate">{ev.property}{ev.client && ` — ${ev.client}`}</p>
                    </div>
                    {ev.overdue && <Badge className="bg-red-500/10 text-red-400 border-0 text-xs shrink-0">Vencida</Badge>}
                    {(ev.deal_id || ev.lead_id) && <ChevronRight size={16} className="text-white/30 shrink-0" />}
                  </div>
                ))}
              </div>
            )}

            {dayTasks.length > 0 && (
              <div className="space-y-2 mb-4">
                <h3 className="text-xs uppercase tracking-wider text-white/50 font-medium">Tareas del dia</h3>
                {dayTasks.map(t => (
                  <div key={t.id} onClick={() => setEditing(t)} data-testid={`day-task-${t.id}`} role="button"
                    className="flex items-center justify-between p-3 bg-[#16302B] rounded-xl border border-white/5 cursor-pointer hover:border-[#A38560]/40 transition-colors">
                    <div className="flex items-center gap-2">
                      <button onClick={e => { e.stopPropagation(); toggleTask(t); }} data-testid={`toggle-task-${t.id}`}>
                        {t.status === 'completada' ? <CheckCircle2 size={18} className="text-[#6BCB77]" /> : <Circle size={18} className="text-white/30" />}
                      </button>
                      <span className={`text-sm ${t.status === 'completada' ? 'line-through text-white/50' : 'text-white'}`}>{t.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {t.due_time && <span className="text-xs text-white/50">{t.due_time}</span>}
                      <span className="text-xs text-white/50">{t.assigned_to_name}</span>
                      <button onClick={e => { e.stopPropagation(); deleteTask(t.id); }} className="text-white/30 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {dayEvents.length === 0 && dayTasks.length === 0 && dayFollowUps.length === 0 && (
              <p className="text-sm text-white/50 py-4">No hay eventos ni tareas para este dia.</p>
            )}
          </div>

          {/* All Tasks */}
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-6">
            <h2 className="font-outfit text-lg font-medium text-white mb-3">Todas las Tareas</h2>
            {tasks.length > 0 ? (
              <div className="space-y-2">
                {tasks.map(t => (
                  <div key={t.id} onClick={() => setEditing(t)} data-testid={`task-row-${t.id}`} role="button"
                    className="flex items-center justify-between gap-2 p-3 bg-[#16302B] rounded-xl border border-white/5 cursor-pointer hover:border-[#A38560]/40 transition-colors">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <button onClick={e => { e.stopPropagation(); toggleTask(t); }} data-testid={`toggle-task-all-${t.id}`}>
                        {t.status === 'completada' ? <CheckCircle2 size={18} className="text-[#6BCB77]" /> : <Circle size={18} className="text-white/30" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <span className={`text-sm block ${t.status === 'completada' ? 'line-through text-white/50' : 'text-white font-medium'}`}>{t.title}</span>
                        {t.description && <span className="text-xs text-white/40 block truncate">{t.description}</span>}
                        {(t.lead_name || t.deal_name) && <span className="text-[11px] text-[#A38560]/80 block truncate">{t.lead_name || t.deal_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {t.due_date && <span className="text-xs text-white/50">{formatDate(t.due_date)}{t.due_time ? ` · ${t.due_time}` : ''}</span>}
                      <span className="text-xs text-[#A38560] hidden sm:inline">{t.assigned_to_name}</span>
                      <button onClick={e => { e.stopPropagation(); deleteTask(t.id); }} className="text-white/30 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-white/50">No hay tareas creadas.</p>
            )}
          </div>
        </div>
      </div>
      <TaskEditDialog task={editing} users={users} isAdmin={user?.role === 'admin'} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}
